import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { toast } from 'react-hot-toast';
import axiosInstance from '../../utils/axios';

// Import all workflow components
import AuditProgressDashboard from './components/AuditProgressDashboard';
import AuditQueue from './components/AuditQueue';
import AuditTrailPanel from './components/AuditTrailPanel';
import ExceptionManagement from './components/ExceptionManagement';
import QuickActionToolbar from './components/QuickActionToolbar';
import AuditFilters from './components/AuditFilters';

const QuarterlyAuditWorkflow = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [auditStats, setAuditStats] = useState({
    total_in_queue: 0,
    pending: 0,
    in_progress: 0,
    verified: 0,
    exceptions: 0,
    escalated: 0,
    completion_percentage: 0,
    total_valuation: 0,
    verified_valuation: 0,
  });

  // Compliance report modal
  const [complianceReport, setComplianceReport] = useState(null);
  const [showComplianceModal, setShowComplianceModal] = useState(false);

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Load audit statistics
  const loadStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/audit/stats');
      if (response.data.success) {
        setAuditStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading audit stats:', error);
    }
  }, []);

  // Handle bulk verification
  const handleBulkVerify = async (selectedAssets) => {
    if (!selectedAssets || selectedAssets.length === 0) {
      toast.error('Please select assets to verify');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post('/audit/bulk-verify', {
        asset_ids: selectedAssets,
        condition: 'Optimal',
        notes: 'Bulk verified via Quarterly Audit Workflow'
      });

      if (response.data.success) {
        toast.success(response.data.message);
        refreshCurrentView();
        loadStats();
      } else {
        toast.error(response.data.message || 'Failed to verify assets');
      }
    } catch (error) {
      console.error('Error in bulk verification:', error);
      toast.error(error.response?.data?.message || 'Failed to verify assets');
    } finally {
      setLoading(false);
    }
  };

  // Handle print sheets
  const handlePrintSheets = () => {
    window.open('/api/audit/print-sheets?format=html', '_blank');
    toast.success('Printable physical audit sheet opened');
  };

  // Handle export reports
  const handleExportReports = async () => {
    try {
      setLoading(true);
      const type = activeView === 'queue' ? 'queue' : activeView === 'trail' ? 'trail' : activeView === 'exceptions' ? 'exceptions' : 'queue';
      const response = await axiosInstance.get('/audit/export', {
        params: { type },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-${type}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV Report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setLoading(false);
    }
  };

  // Handle generate compliance report
  const handleGenerateCompliance = async (reportData = null) => {
    if (reportData) {
      setComplianceReport(reportData);
      setShowComplianceModal(true);
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get('/audit/report');
      
      if (response.data.success) {
        setComplianceReport(response.data.data);
        setShowComplianceModal(true);
        toast.success('Quarterly Compliance Documentation generated');
      } else {
        toast.error('Failed to generate compliance report');
      }
    } catch (error) {
      console.error('Error generating compliance report:', error);
      toast.error('Failed to generate compliance report');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    refreshCurrentView(newFilters);
  };

  // Refresh current view
  const refreshCurrentView = (additionalFilters = {}) => {
    const event = new CustomEvent('refreshAuditView', { 
      detail: { ...filters, ...additionalFilters } 
    });
    window.dispatchEvent(event);
  };

  // View tabs configuration
  const viewTabs = [
    { key: 'dashboard', label: 'Progress Dashboard', icon: 'BarChart3' },
    { key: 'queue', label: 'Audit Queue', icon: 'List' },
    { key: 'trail', label: 'Audit Trail', icon: 'History' },
    { key: 'exceptions', label: 'Exceptions Tracker', icon: 'AlertTriangle' }
  ];

  // Render active view
  const renderActiveView = () => {
    switch (activeView) {
      case 'dashboard':
        return <AuditProgressDashboard onRefresh={loadStats} />;
      case 'queue':
        return <AuditQueue onBulkVerify={handleBulkVerify} filters={filters} />;
      case 'trail':
        return <AuditTrailPanel filters={filters} />;
      case 'exceptions':
        return <ExceptionManagement filters={filters} />;
      default:
        return <AuditProgressDashboard onRefresh={loadStats} />;
    }
  };

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      loadStats();
    };
    window.addEventListener('refreshAuditStats', handleRefresh);
    return () => window.removeEventListener('refreshAuditStats', handleRefresh);
  }, [loadStats]);

  const currentQuarter = `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`;
  const currentYear = new Date().getFullYear();

  const formatINR = (amt) => {
    return '₹' + Number(amt || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={handleToggleSidebar} 
      />
      <main className={`pt-16 transition-layout ${
        sidebarCollapsed ? 'ml-16' : 'ml-72'
      }`}>
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <Breadcrumb />
              <div className="flex items-center space-x-3 mt-2">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Icon name="ClipboardCheck" size={24} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Quarterly Audit Workflow</h1>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {currentQuarter} {currentYear} Enterprise Asset Physical Verification, Custody & Compliance
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 flex-wrap gap-2">
              <div className="text-right text-xs bg-muted/20 border border-border px-3 py-1.5 rounded-lg">
                <div className="text-foreground font-semibold">
                  Active Cycle: {currentQuarter} {currentYear}
                </div>
                <div className="text-muted-foreground text-[11px]">
                  Physical verification cycle in progress
                </div>
              </div>

              <Button 
                variant="default" 
                onClick={() => handleGenerateCompliance()}
                loading={loading}
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-semibold"
              >
                <Icon name="FileCheck" size={15} className="mr-1.5" />
                Generate Compliance Report
              </Button>
            </div>
          </div>

          {/* Quick Stats KPI Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-card border border-border rounded-xl p-3.5 shadow-subtle text-center">
              <p className="text-2xl font-bold font-mono text-card-foreground">{auditStats.total_in_queue || 0}</p>
              <p className="text-xs text-muted-foreground font-medium mt-0.5">Total Assets</p>
              <p className="text-[10px] text-muted-foreground font-mono mt-1">{formatINR(auditStats.total_valuation)}</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-3.5 shadow-subtle text-center">
              <p className="text-2xl font-bold font-mono text-amber-500">{auditStats.pending || 0}</p>
              <p className="text-xs text-muted-foreground font-medium mt-0.5">Pending Audit</p>
              <p className="text-[10px] text-amber-600 font-semibold mt-1">Requires Physical Check</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-3.5 shadow-subtle text-center">
              <p className="text-2xl font-bold font-mono text-indigo-500">{auditStats.in_progress || 0}</p>
              <p className="text-xs text-muted-foreground font-medium mt-0.5">In Progress / Transit</p>
              <p className="text-[10px] text-indigo-600 font-semibold mt-1">Location Moving</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-3.5 shadow-subtle text-center">
              <p className="text-2xl font-bold font-mono text-emerald-500">{auditStats.verified || 0}</p>
              <p className="text-xs text-muted-foreground font-medium mt-0.5">Verified</p>
              <p className="text-[10px] text-emerald-600 font-mono mt-1">{formatINR(auditStats.verified_valuation)}</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-3.5 shadow-subtle text-center">
              <p className="text-2xl font-bold font-mono text-rose-500">{auditStats.exceptions || 0}</p>
              <p className="text-xs text-muted-foreground font-medium mt-0.5">Exceptions</p>
              <p className="text-[10px] text-rose-600 font-semibold mt-1">{auditStats.exceptions > 0 ? 'Discrepancy Open' : 'Zero Open'}</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-3.5 shadow-subtle text-center">
              <p className="text-2xl font-bold font-mono text-purple-500">{auditStats.completion_percentage || 0}%</p>
              <p className="text-xs text-muted-foreground font-medium mt-0.5">Cycle Progress</p>
              <div className="w-full bg-muted rounded-full h-1.5 mt-1.5 overflow-hidden">
                <div 
                  className="bg-purple-500 h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(auditStats.completion_percentage || 0, 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Quick Action Toolbar */}
          <QuickActionToolbar
            onPrintSheets={handlePrintSheets}
            onExportReports={handleExportReports}
            onGenerateCompliance={handleGenerateCompliance}
            onRefresh={loadStats}
            loading={loading}
          />

          {/* Audit Filters */}
          <AuditFilters onFiltersChange={handleFiltersChange} />

          {/* View Tabs */}
          <div className="bg-card border border-border rounded-xl shadow-subtle overflow-hidden">
            <div className="border-b border-border bg-muted/10 overflow-x-auto">
              <nav className="flex space-x-1 p-1.5 min-w-max">
                {viewTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveView(tab.key)}
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                      activeView === tab.key
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={tab.icon} size={15} />
                    <span>{tab.label}</span>
                    {tab.key === 'exceptions' && auditStats.exceptions > 0 && (
                      <span className="ml-1 bg-rose-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                        {auditStats.exceptions}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
            
            <div className="p-6">
              {renderActiveView()}
            </div>
          </div>
        </div>
      </main>

      {/* ========================================================================= */}
      {/* COMPLIANCE REPORT MODAL */}
      {/* ========================================================================= */}
      {showComplianceModal && complianceReport && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-6 border-b border-border bg-gradient-to-r from-primary/10 to-transparent flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shadow-md">
                  <Icon name="FileCheck" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-lg">
                    Quarterly Compliance Audit Documentation
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {complianceReport.period} • Generated on {complianceReport.generation_date}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowComplianceModal(false)}
                className="text-muted-foreground hover:text-foreground p-1 rounded-lg"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border border-border bg-muted/10">
                  <span className="text-xs text-muted-foreground block mb-1">Compliance Score</span>
                  <span className="text-3xl font-extrabold font-mono text-primary">
                    {complianceReport.compliance_score}
                  </span>
                  <span className="text-xs text-muted-foreground block mt-1">
                    {complianceReport.compliance_status}
                  </span>
                </div>

                <div className="p-4 rounded-xl border border-border bg-muted/10">
                  <span className="text-xs text-muted-foreground block mb-1">Audited Asset Valuation</span>
                  <span className="text-2xl font-bold font-mono text-emerald-600">
                    {complianceReport.valuation_audited}
                  </span>
                  <span className="text-xs text-muted-foreground block mt-1">
                    Physical hardware confirmed
                  </span>
                </div>
              </div>

              <div className="divide-y divide-border border border-border rounded-xl overflow-hidden">
                <div className="p-3 bg-muted/20 flex justify-between text-xs font-semibold text-foreground">
                  <span>Audit Parameter</span>
                  <span>Inspection Metric</span>
                </div>
                <div className="p-3 flex justify-between text-xs">
                  <span className="text-muted-foreground">Total Company Equipment</span>
                  <span className="font-semibold text-foreground">{complianceReport.total_inventory} Units</span>
                </div>
                <div className="p-3 flex justify-between text-xs">
                  <span className="text-muted-foreground">Physically Verified Assets</span>
                  <span className="font-semibold text-emerald-600">{complianceReport.verified_count} Units</span>
                </div>
                <div className="p-3 flex justify-between text-xs">
                  <span className="text-muted-foreground">Open Discrepancies & Exceptions</span>
                  <span className="font-semibold text-rose-600">{complianceReport.exceptions_count} Open</span>
                </div>
                <div className="p-3 flex justify-between text-xs">
                  <span className="text-muted-foreground">Authorized Audit Signoff</span>
                  <span className="font-semibold text-foreground">{complianceReport.auditor_signoff}</span>
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-border bg-muted/10 flex items-center justify-between">
              <button
                onClick={() => window.print()}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center font-medium"
              >
                <Icon name="Printer" size={14} className="mr-1.5" />
                Print Certificate
              </button>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowComplianceModal(false)}
                >
                  Close
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    handleExportReports();
                    setShowComplianceModal(false);
                  }}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-semibold"
                >
                  <Icon name="Download" size={14} className="mr-1.5" />
                  Download Full CSV
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuarterlyAuditWorkflow;
